import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthApiRoutes } from './auth-api.routes';
import { AuthUser } from './auth-user.model';

export interface LoginCredentials {
  usernameOrEmail: string;
  password: string;
  rememberMe: boolean;
}

interface LoginResponse {
  user: AuthUser;
}

export interface ForgotPasswordResponse {
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly userState = signal<AuthUser | null>(null);
  private loadPromise: Promise<AuthUser | null> | null = null;
  private stateVersion = 0;

  readonly currentUser = this.userState.asReadonly();
  readonly isAuthenticated = computed(() => this.userState() !== null);

  ensureCurrentUser(): Promise<AuthUser | null> {
    if (this.userState()) {
      return Promise.resolve(this.userState());
    }

    const version = this.stateVersion;
    this.loadPromise ??= firstValueFrom(this.http.get<AuthUser>(AuthApiRoutes.CurrentUser))
      .then((user) => {
        if (version === this.stateVersion) {
          this.userState.set(user);
          return user;
        }

        return null;
      })
      .catch(() => {
        if (version === this.stateVersion) {
          this.userState.set(null);
          return null;
        }

        return null;
      })
      .finally(() => {
        if (version === this.stateVersion) {
          this.loadPromise = null;
        }
      });

    return this.loadPromise;
  }

  async login(credentials: LoginCredentials): Promise<AuthUser> {
    const version = ++this.stateVersion;
    this.userState.set(null);
    this.loadPromise = null;

    await firstValueFrom(this.http.post<LoginResponse>(AuthApiRoutes.Login, credentials));

    const user = await this.loadCurrentUser(version);

    if (!user || version !== this.stateVersion || this.userState() !== user) {
      throw new Error('The authenticated session could not be initialized.');
    }

    return user;
  }

  forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    return firstValueFrom(
      this.http.post<ForgotPasswordResponse>(AuthApiRoutes.ForgotPassword, { email }),
    );
  }

  resetPassword(
    token: string,
    verificationCode: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(AuthApiRoutes.ResetPassword, {
        token,
        verificationCode,
        newPassword,
        confirmPassword,
      }),
    );
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.http.post<void>(AuthApiRoutes.Logout, null));
    } finally {
      this.clearCurrentUser();
    }
  }

  hasPermission(permission: string): boolean {
    return this.userState()?.permissions.includes(permission) ?? false;
  }

  hasAnyPermission(permissions: readonly string[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  clearCurrentUser(): void {
    this.stateVersion++;
    this.userState.set(null);
    this.loadPromise = null;
  }

  async refreshCurrentUser(): Promise<AuthUser | null> {
    const version = ++this.stateVersion;
    this.loadPromise = null;
    return this.loadCurrentUser(version);
  }

  async updateProfile(
    value: Pick<
      AuthUser,
      | 'username'
      | 'email'
      | 'phoneNumber'
      | 'preferredLanguage'
      | 'firstName'
      | 'lastName'
      | 'street'
      | 'houseNumber'
      | 'postalCode'
      | 'cityId'
    >,
    photo?: File | null,
  ): Promise<AuthUser> {
    await firstValueFrom(this.http.put<AuthUser>(AuthApiRoutes.Profile, value));
    if (photo) {
      const form = new FormData();
      form.append('photo', photo);
      await firstValueFrom(this.http.post<AuthUser>(AuthApiRoutes.ProfilePhoto, form));
    }

    const user = await this.refreshCurrentUser();
    if (!user) {
      throw new Error('The updated profile could not be reloaded.');
    }

    return user;
  }

  /**
   * PUT /api/Auth/language. The backend validates against SupportedLanguages and
   * answers `language.unsupported` for anything outside {en, sw, fr}.
   */
  async updatePreferredLanguage(language: string): Promise<AuthUser | null> {
    await firstValueFrom(
      this.http.put<{ preferredLanguage: string }>(AuthApiRoutes.Language, {
        language,
      }),
    );

    return this.refreshCurrentUser();
  }

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(AuthApiRoutes.ChangePassword, { currentPassword, newPassword }),
    );
  }

  private loadCurrentUser(version: number): Promise<AuthUser | null> {
    return firstValueFrom(this.http.get<AuthUser>(AuthApiRoutes.CurrentUser))
      .then((user) => {
        if (version !== this.stateVersion) {
          return this.userState();
        }

        this.userState.set(user);
        return user;
      })
      .catch(() => {
        if (version === this.stateVersion) {
          this.userState.set(null);
        }
        return null;
      });
  }
}
